/**
 * Session Redux Reducer
 */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ISessionState, initialSessionState } from "./types";
import thunks from "./thunks";
import { UserSession } from "@/types/session";

export const slice = createSlice({
  name: "session",
  initialState: initialSessionState,
  reducers: {
    // Manual actions
    setSessions: (state, action: PayloadAction<UserSession[]>) => {
      state.activeSessions = action.payload;
    },
    setCurrentSession: (state, action: PayloadAction<UserSession | null>) => {
      state.currentSession = action.payload;
    },
    setSharingDetected: (state, action: PayloadAction<boolean>) => {
      state.sharingDetected = action.payload;
    },
    setAnomalies: (state, action: PayloadAction<string[]>) => {
      state.anomalies = action.payload;
    },
    setIsMonitoring: (state, action: PayloadAction<boolean>) => {
      state.isMonitoring = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetSession: (state) => {
      return initialSessionState;
    },
  },
  extraReducers: (builder) => {
    // Fetch Active Sessions
    builder
      .addCase(thunks.fetchActiveSessions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchActiveSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.activeSessions = action.payload.data || [];
        state.lastChecked = new Date().toISOString();
      })
      .addCase(thunks.fetchActiveSessions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload
          ? JSON.stringify(action.payload)
          : "Failed to fetch sessions";
      });

    // Check Account Sharing
    builder
      .addCase(thunks.checkAccountSharing.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.checkAccountSharing.fulfilled, (state, action) => {
        state.loading = false;
        const { is_suspicious, anomalies, sessions } =
          action.payload.data || {};
        state.sharingDetected = is_suspicious || false;
        state.anomalies = anomalies || [];
        if (sessions) {
          state.activeSessions = sessions;
        }
        state.lastChecked = new Date().toISOString();
      })
      .addCase(thunks.checkAccountSharing.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload
          ? JSON.stringify(action.payload)
          : "Failed to check account sharing";
      });

    // Logout From Device
    builder
      .addCase(thunks.logoutFromDevice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.logoutFromDevice.fulfilled, (state) => {
        state.loading = false;
        // Remove the logged out session from the list
      })
      .addCase(thunks.logoutFromDevice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload
          ? JSON.stringify(action.payload)
          : "Failed to logout from device";
      });

    // Logout All Other Devices
    builder
      .addCase(thunks.logoutAllOtherDevices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.logoutAllOtherDevices.fulfilled, (state) => {
        state.loading = false;
        // Keep only current session
      })
      .addCase(thunks.logoutAllOtherDevices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload
          ? JSON.stringify(action.payload)
          : "Failed to logout from other devices";
      });

    // Update Session Activity
    builder
      .addCase(thunks.updateSessionActivity.pending, (state) => {
        // Don't set loading for activity updates
      })
      .addCase(thunks.updateSessionActivity.fulfilled, (state) => {
        // Activity updated silently
      })
      .addCase(thunks.updateSessionActivity.rejected, (state) => {
        // Handle silently
      });
  },
});

export const {
  setSessions,
  setCurrentSession,
  setSharingDetected,
  setAnomalies,
  setIsMonitoring,
  clearError,
  resetSession,
} = slice.actions;

const SESSION = {
  slice,
  thunks,
};

export default SESSION;
